import {
    Image,
    rem,
    Box,
    AppShell,
    Center,
    Text,
    Stack
} from '@mantine/core';
import type { ReactNode } from 'react';
import { 
    IconHome,
    IconMailbox,
    IconCalendarEvent,
    IconSettings,
    IconLayoutSidebarLeftCollapse,
    IconLayoutSidebarRightCollapse,
    IconUsersGroup,
    IconCategory,
    IconActivity,
    IconBrush
} from '@tabler/icons-react';

import { ProfileSummary } from './ProfileSummary';
import { MainLink } from './MainLink';

import { MainButton } from './MainButton';

import { version } from '../../../package.json'
import { t } from 'i18next';

import logo from '@/assets/studiocast_rbg_couleur.svg';
import logoSimple from '@/assets/studiocast.svg';
import { ROLES, useAuthorization } from '@/lib/authorization';

export type NavProps = {
    open: boolean;
    onToggleOpen: () => void;
}

type NavigationItemProps = {
    href: string;
    icon: ReactNode;
    color: string;
    label: string;
}

export const Nav = ({ open, onToggleOpen }: NavProps) => {
    const { checkAccess } = useAuthorization();

    const data = [
        { href: '/', icon: <IconHome />, label: 'menu.home' },
        { href: '/events', icon: <IconCalendarEvent />, label: 'event.event_other' },
        checkAccess({ allowedRoles: [ROLES.ADMIN]}) && { href: '/monitoring', icon: <IconActivity />, label: 'menu.monitoring' },
        checkAccess({ allowedRoles: [ROLES.ADMIN]}) && { href: '/communications', icon: <IconMailbox />, label: 'menu.communications' },
        checkAccess({ allowedRoles: [ROLES.ADMIN]}) && { href: '/categories', icon: <IconCategory />, label: 'menu.categories' },
        checkAccess({ allowedRoles: [ROLES.ADMIN]}) && { href: '/personalization', icon: <IconBrush />, label: 'menu.personalization' },
        checkAccess({ allowedRoles: [ROLES.ADMIN]}) && { href: '/users', icon: <IconUsersGroup />, label: 'menu.users' },
        { href: '/settings', icon: <IconSettings />, label: 'menu.settings' }
    ].filter(Boolean) as NavigationItemProps[]

    return (
        <AppShell.Navbar p="md">
            <AppShell.Section mt="xs">
                <Image src={open ? logo : logoSimple} />
            </AppShell.Section>
            <AppShell.Section mt="md" grow>
                <Stack gap={0}>
                {
                    data.map((link) => (
                        <MainLink
                            {...link} 
                            color="blue"
                            open={open} 
                            key={link.label} />
                    ))
                }
                </Stack>
            </AppShell.Section>
            <AppShell.Section>
                <Center>
                {
                    open 
                        ? <Text c="dimmed">{"version " + version}</Text>
                        : <Text fz={'sm'} c="dimmed">{"v " + version}</Text>
                }
                </Center>
            </AppShell.Section>
            <AppShell.Section mt="xs">
                <Box
                    style={{
                        paddingTop: 'var(--mantine-spacing-sm)',
                        borderTop: `${rem(1)} solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))`,
                    }}>
                    <Box visibleFrom='md' pb="md">
                        <MainButton 
                            handler={onToggleOpen} 
                            icon={open ? <IconLayoutSidebarLeftCollapse /> : <IconLayoutSidebarRightCollapse />} 
                            color='blue'
                            label={t('menu.collapse')}
                            open={open} />
                    </Box>
                    <ProfileSummary open={open} />
                </Box>
            </AppShell.Section>
        </AppShell.Navbar>
    )
}